import React, { useEffect, useMemo, useRef, useState } from "react";
import { injectIntl } from "react-intl";
import { Drawer, Input, Popover, Tooltip } from "antd";
import { CheckOutlined, DownOutlined, SearchOutlined } from "@ant-design/icons";
import { buildSenders, customerSenders, highlightParts, matches, parseValue, valueOf } from "./senders";
import "./SelectSender.less";

const isPhone = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 767px)").matches;

const Highlight = ({text, query}) => (
    <>
        {highlightParts(text, query).map((part, index) => (typeof part === "string"
            ? <React.Fragment key={index}>{part}</React.Fragment>
            : <mark key={index}>{part.mark}</mark>))}
    </>
);

// The chat sender: an SMS / WhatsApp switch, then the number, picked from a searchable list of that channel's
// senders (number, name or sender ID). Numbers this customer used come first. Value: `<number>` (SMS) or
// `<number>-whatsapp`.
const SelectSender = ({selectedValue, senderList, updateSenderNumber, messages, intl}) => {
    const t = (id, defaultMessage, values) => intl.formatMessage({id, defaultMessage}, values);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const [phone, setPhone] = useState(isPhone);
    const searchRef = useRef(null);
    const listRef = useRef(null);

    const senders = useMemo(() => buildSenders(senderList), [senderList]);
    const forCustomer = useMemo(() => customerSenders(messages, senders), [messages, senders]);

    const selected = useMemo(() => {
        if (selectedValue) {
            const {number, channel} = parseValue(selectedValue);
            const sender = senders.find(item => item.number === number);
            if (sender) return {sender, channel};
        }
        const sender = senders.find(item => item.isDefault) || senders[0];
        return sender ? {sender, channel: sender.sms ? "sms" : "whatsapp"} : null;
    }, [selectedValue, senders]);

    const channel = selected ? selected.channel : "sms";
    const hasWhatsapp = senders.some(sender => sender.whatsapp);
    const channelName = value => (value === "whatsapp" ? "WhatsApp" : "SMS");

    // Groups in display order; `rows` is the flat list the keyboard moves through.
    const {groups, rows} = useMemo(() => {
        const visible = senders.filter(sender => sender[channel] && matches(sender, query));
        const customer = forCustomer
            .map(item => ({...item, sender: visible.find(sender => sender.number === item.number)}))
            .filter(item => item.sender);
        const taken = new Set(customer.map(item => item.number));
        const numbers = visible.filter(sender => !sender.isSenderId && !taken.has(sender.number));
        const ids = visible.filter(sender => sender.isSenderId && !taken.has(sender.number));
        const list = [
            {key: "customer", title: t("chats.senderForCustomer", "For this customer"), items: customer.map(item => ({sender: item.sender, hint: item}))},
            {key: "numbers", title: t("chats.senderNumbers", "Phone numbers"), items: numbers.map(sender => ({sender}))},
            {key: "ids", title: t("chats.senderIds", "Sender IDs"), note: t("chats.senderSmsOnly", "SMS only"), items: ids.map(sender => ({sender}))},
        ].filter(group => group.items.length);
        return {groups: list, rows: list.flatMap(group => group.items)};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [senders, forCustomer, query, channel, intl]);

    // Switching channel keeps the number when it has that channel, else takes the one this customer used, then the
    // default, then the first.
    const switchChannel = next => {
        if (next === channel) return;
        const able = senders.filter(sender => sender[next]);
        const customer = forCustomer.map(item => able.find(sender => sender.number === item.number)).find(Boolean);
        const sender = able.find(item => item.number === selected?.sender.number)
            || customer || able.find(item => item.isDefault) || able[0];
        if (sender) updateSenderNumber(valueOf(sender.number, next));
    };

    useEffect(() => {
        if (!open) return undefined;
        setQuery("");
        const index = Math.max(0, rows.findIndex(item => item.sender.number === selected?.sender.number));
        setActive(index);
        // On phones the keyboard would cover the list, so search is focused only on desktop.
        const timer = !isPhone() && setTimeout(() => searchRef.current?.focus(), 60);
        return () => timer && clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => setActive(0), [query]);

    useEffect(() => {
        const row = listRef.current && listRef.current.querySelector(".sender-picker__row.is-active");
        if (row && row.scrollIntoView) row.scrollIntoView({block: "nearest"});
    }, [active]);

    const choose = sender => {
        updateSenderNumber(valueOf(sender.number, channel));
        setOpen(false);
        setPhone(false);
    };

    const onKeyDown = event => {
        const item = rows[active];
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (!rows.length) return;
            setActive((active + (event.key === "ArrowDown" ? 1 : -1) + rows.length) % rows.length);
        } else if (event.key === "Enter" && item) {
            event.preventDefault();
            choose(item.sender);
        } else if (event.key === "Escape") {
            setOpen(false);
        }
    };

    const hintText = hint => {
        if (!hint) return null;
        const channel = hint.channel === "whatsapp" ? "WhatsApp" : "SMS";
        return hint.reason === "wrote"
            ? t("chats.senderCustomerWrote", "they last wrote here on {channel}", {channel})
            : t("chats.senderTeamReplied", "you last replied from here on {channel}", {channel});
    };

    let rowIndex = -1;
    const panel = (
        <div className="sender-picker" onKeyDown={onKeyDown}>
            <div className="sender-picker__head">
                <Input
                    ref={searchRef}
                    className="sender-picker__search"
                    allowClear
                    prefix={<SearchOutlined/>}
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    placeholder={channel === "whatsapp"
                        ? t("chats.senderSearchWhatsapp", "Search WhatsApp senders")
                        : t("chats.senderSearch", "Search number, name or sender ID")}
                    aria-label={t("chats.senderSearch", "Search number, name or sender ID")}
                />
            </div>
            <div className="sender-picker__list" ref={listRef} role="listbox">
                {groups.map(group => (
                    <div key={group.key} className="sender-picker__group">
                        <div className="sender-picker__group-title">
                            <span>{group.title}</span>
                            {group.note ? <span>{group.note}</span> : null}
                        </div>
                        {group.items.map(item => {
                            rowIndex += 1;
                            const index = rowIndex;
                            const {sender, hint} = item;
                            const isSelected = selected?.sender.number === sender.number && selected.channel === channel;
                            const sub = [
                                sender.isSenderId ? t("chats.senderIdHint", "Sender ID · customers can't reply") : sender.label,
                                hintText(hint)
                            ].filter(Boolean).join(" · ");
                            return (
                                <div
                                    key={sender.number}
                                    role="option"
                                    aria-selected={isSelected}
                                    className={`sender-picker__row${isSelected ? " is-selected" : ""}${active === index ? " is-active" : ""}`}
                                    onMouseEnter={() => active !== index && setActive(index)}
                                    onClick={() => choose(sender)}
                                >
                                    <span className="sender-picker__text">
                                        <span className="sender-picker__title">
                                            <Highlight text={sender.title} query={query}/>
                                            {sender.isDefault ? <span className="sender-picker__tag">{t("chats.senderDefault", "Default")}</span> : null}
                                        </span>
                                        {sub ? <span className="sender-picker__sub">{sub}</span> : null}
                                    </span>
                                    {isSelected ? <CheckOutlined className="sender-picker__check" aria-hidden="true"/> : null}
                                </div>
                            );
                        })}
                    </div>
                ))}
                {!rows.length ? (
                    <div className="sender-picker__empty">
                        <strong>
                            {query
                                ? t("chats.senderNoMatchChannel", "No {channel} sender matches “{query}”", {query, channel: channelName(channel)})
                                : t("chats.senderNoneChannel", "No {channel} senders", {channel: channelName(channel)})}
                        </strong>
                    </div>
                ) : null}
            </div>
            {!phone ? (
                <div className="sender-picker__keys">
                    <span>↑↓ {t("chats.senderKeyNumber", "number")}</span>
                    <span>↵ {t("chats.senderKeySelect", "select")}</span>
                </div>
            ) : null}
        </div>
    );

    const trigger = (
        <button
            type="button"
            className="select-sender sender-picker__trigger"
            aria-haspopup="listbox"
            aria-expanded={open}
            title={t("chats.sender", "Sender")}
        >
            {selected ? (
                <>
                    {!hasWhatsapp ? <span className="sender-picker__trigger-channel mobile-hide">{t("chats.sender", "Sender")}:</span> : null}
                    <span className="sender-picker__trigger-number">{selected.sender.title}</span>
                    {selected.sender.label ? <span className="sender-picker__trigger-label mobile-hide">{selected.sender.label}</span> : null}
                </>
            ) : (
                <span className="sender-picker__trigger-number">{t("chats.sender", "Sender")}</span>
            )}
            <DownOutlined className="sender-picker__caret"/>
        </button>
    );

    const openChange = next => {
        setPhone(next && isPhone());
        setOpen(next);
    };

    const toggle = hasWhatsapp ? (
        <span className="sender-channel" role="radiogroup" aria-label={t("chats.senderChannel", "Channel")}>
            {["sms", "whatsapp"].map(value => {
                const able = senders.some(sender => sender[value]);
                const button = (
                    <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={channel === value}
                        disabled={!able}
                        className={`sender-channel__option is-${value}${channel === value ? " is-on" : ""}`}
                        onClick={() => switchChannel(value)}
                    >
                        {value === "whatsapp" ? (
                            <>
                                <span className="sender-channel__long">WhatsApp</span>
                                <span className="sender-channel__short" aria-hidden="true">WA</span>
                            </>
                        ) : "SMS"}
                    </button>
                );
                return able ? button : <Tooltip key={value} title={t("chats.senderNoChannel", "No {channel} number on this account", {channel: channelName(value)})}>{button}</Tooltip>;
            })}
        </span>
    ) : null;

    return (
        <span className="sender-select">
            {toggle}
            <Popover
                open={open && !phone}
                onOpenChange={openChange}
                trigger="click"
                placement="topLeft"
                arrow={false}
                overlayClassName="sender-picker-popover"
                content={panel}
                destroyTooltipOnHide
            >
                {trigger}
            </Popover>
            {phone ? (
                <Drawer
                    open={open}
                    placement="bottom"
                    height="auto"
                    closable={false}
                    onClose={() => setOpen(false)}
                    rootClassName="sender-picker-sheet"
                    title={t("chats.senderSendFromChannel", "Send {channel} from", {channel: channelName(channel)})}
                >
                    {panel}
                </Drawer>
            ) : null}
        </span>
    );
};

export default injectIntl(SelectSender);
